/**
 * Grown-up screens in Traditional Chinese, Taiwan usage (25 Oct 2026).
 *
 * Keyed by the exact English text in the code. Written by Claude; to be read
 * by a native speaker before relying on it. A line missing here shows in
 * English, so a partial file is safe.
 */
export const ZH_TW: Record<string, string> = {
  // Settings: frame and tabs
  'Settings': '設定',
  'Children': '孩子',
  'Word requests': '想學的字',
  'Books': '書本',
  'Progress': '學習進度',
  'Recent learning': '最近學習',
  'Your account': '您的帳號',
  'To open Settings.': '用來開啟「設定」。',

  // Children tab
  'School year': '年級',
  'Offset (−3 to +3)': '程度調整（−3 到 +3）',
  'Raise the offset if the work feels too easy, lower it if too hard.':
    '如果題目太簡單，就調高；太難就調低。',
  'Save': '儲存',
  'Saved': '已儲存',
  'Archive': '封存',
  'Archiving hides a child from Home and keeps every answer they have ever given. There is no delete, on purpose — three years of practice is not something an app should make easy to throw away.':
    '封存會把孩子從首頁隱藏，但保留他做過的每一個答案。這裡刻意不提供刪除：三年的練習不該被程式輕易丟掉。',
  'No children yet.': '尚未新增孩子。',
  'The app aims for 85 to 90%. New words will move to the new level over the next few practices.':
    '本程式的目標是答對 85% 到 90%。接下來幾次練習，新的單字會換到新的程度。',
  'Raise the offset to': '把程度調高到',
  'Lower the offset to': '把程度調低到',

  // Progress tab
  'This week': '本週',
  'This month': '本月',
  'Loading…': '載入中…',
  'That did not load. Try again in a moment.': '載入失敗，請稍後再試。',
  'Minutes': '分鐘',
  'Days practised': '練習天數',
  'Words grown': '已學會的字',
  'New words met': '新認識的字',
  'Words fully grown, last 8 weeks': '最近 8 週完全學會的字',
  'A word is grown once your child has got it right in all three kinds of question (knowing it, spelling it, using it), six right answers in all, over at least three practices. A grown word stays grown, so this only goes up.':
    '孩子在三種題目（認得、拼寫、使用）都答對，總共答對六次、分布在至少三次練習中，這個字就算學會。學會的字不會再被扣掉，所以這個數字只會上升。',
  'Needs a little help': '需要多加練習',
  'Word': '單字',
  'Met': '遇見次數',
  'Right on their own': '自己答對',
  'These come back more often on their own. Using one at dinner helps too.':
    '這些字會自動更常出現。吃飯時用用看也很有幫助。',
  'See every word in Recent learning': '在「最近學習」查看所有單字',
  "This week's words": '本週單字',
  'Not enough yet': '資料還不夠',
  'Hardest words': '最難的字',

  // Account tab
  'Progress emails': '學習進度電子郵件',
  'How often:': '發送頻率：',
  'Off': '關閉',
  'Daily': '每天',
  'Weekly': '每週',
  'Monthly': '每月',
  "See this week's email": '看本週的電子郵件',
  'Subscription': '訂閱',
  'Your family has Word Sprout free.': '您的家庭可以免費使用 Word Sprout。',
  'No subscription.': '尚未訂閱。',
  'Change card or cancel': '更換信用卡或取消訂閱',
  'How your data is handled': '您的資料如何處理',
  'What we keep:': '我們保留的資料：',
  "your sign-in, and for each child a name, school year, picture and every answer they give. It is kept in the app's own database on Google Cloud.":
    '您的登入資料，以及每個孩子的名字、年級、圖像和每一個答案。這些都存在本程式在 Google Cloud 上自己的資料庫裡。',
  'What leaves the app:': '會傳送至本程式外的資料：',
  "a word or a book number, when you look one up, and the emails we send you, which include your child's first name and progress. No AI runs while a child practises.": "您查詢時的單字或書號，以及我們寄給您的電子郵件（內含孩子的名字和學習進度）。孩子練習時不會使用 AI。",
  "You can delete everything on this page. To see what we keep about your child, email us at hello@wordsprout.net.": "您可以在這個頁面刪除全部資料。想查看我們保存了孩子的哪些資料，請寫信到 hello@wordsprout.net。",
  'Show where data is stored': '顯示資料存放的位置',
  'Turn labels on': '開啟資料標籤',
  'Turn labels off': '關閉資料標籤',
  'Delete everything': '刪除全部資料',
  "Removes the account, every child and every answer. This cannot be undone.": "會刪除帳號、每個孩子和每一個答案，而且無法復原。",
  'Type DELETE to confirm': '輸入 DELETE 以確認',

  // Language card and set-up question
  'Do you speak a language other than English at home?': '您在家裡會說英文以外的語言嗎？',
  'It helps us show the grown-up screens in your language. The children’s practice stays in English, because English is what they are learning.':
    '這能讓大人的畫面用您的語言顯示。孩子的練習仍然是英文，因為他們正在學英文。',
  'Show the grown-up screens in': '大人畫面的顯示語言：',
  'English': '英文',
  'Not yet available in this language; the screens stay in English for now.':
    '這個語言還沒有翻譯，畫面暫時仍是英文。',
  'That did not save. Try again in a moment.': '儲存失敗，請稍後再試。',

  // Word requests (Settings)
  "Words your children asked to learn that Word Sprout did not know yet. Add the ones you are happy with; they join that child's words.":
    '孩子想學、但 Word Sprout 還沒有的字。加入您同意的字，它們就會加進那個孩子的單字裡。',
  'Looking…': '查詢中…',
  'Nothing waiting. Words your children ask for will show up here.': '目前沒有待處理的字。孩子想學的字會出現在這裡。',
  'Stopped before it reached you': '還沒送到您這裡就被擋下了',
  'A check said no, either because the word is on the block list or because its dictionary entry is. Your child was told it is not one for this app and nothing else. You can overrule any of these.':
    '檢查沒有通過，因為這個字或它的字典解釋在封鎖清單上。孩子只會被告知這個字不適合本程式，其他什麼都不會說。您可以推翻其中任何一個決定。',
  'Asked by': '提出者：',
  'No dictionary entry — it was stopped before we looked.': '沒有字典解釋：查詢之前就被擋下了。',
  'Meaning from': '解釋來源',
  'Not this one': '不加入',
  'Add a word yourself': '自行新增單字',
  'Goes in straight away. The same checks still run — being the grown-up is not a reason to build a vocabulary app around weapons.':
    '會立刻加入，但仍會做同樣的檢查：大人加的字也一樣要合適。',
  'One word': '輸入單一單字',

  // Books (Settings)
  'Sort:': '排序：',
  'No books yet. Pick a child above to add one.': '尚未新增書籍。請在上方選取孩子以新增書本。',
  'There is no camera on this device. The number under the barcode works everywhere.':
    '這台裝置沒有相機。條碼下方的號碼在任何裝置都能使用。',
  'The name is editable — catalogue entries are written by volunteers and are sometimes in the wrong case.':
    '書名可以修改：書目資料由志工填寫，大小寫有時不正確。',
  'Add it': '加入',
  'Not that one': '不是這本',
  'Add': '加入',
  'Stop the camera': '關閉相機',
  'Next to use': '下一本',
  'Not for us': '不適合我們',
  'Take it off': '移除',
  'Next:': '下一本：',
  'Yes to all': '全部同意',
  'These came from the library. Words are only picked from books you say yes to.':
    '這些書來自圖書館。只會從您同意的書裡挑選單字。',
  'Yes, use it': '確認使用',
  'What is it?': '作品類型',

  // Children's tablets (device codes)
  "Set up a child's tablet": '設定孩子的平板',
  'your own phone': '您自己的手機',
  'Add a device': '新增裝置',
  'Device code': '裝置代碼',
  'Name this tablet (so you can find it in Settings)': '替這台平板取個名字（方便在設定中找到）',
  'Cancel': '取消',
  "Children's tablets": '孩子的平板',
  'Use a device code': '使用裝置代碼',
  'Remove': '移除',
  'Tablet name': '平板名稱',
  "It was signed in with a device code, so no Google account is stored on it. Billing, deleting the account and adding tablets are done on your own phone.": "這台平板是用裝置代碼登入的，所以上面沒有儲存 Google 帳號。付費、刪除帳號和新增平板，請在您自己的手機上操作。",

  // Grown-up PIN
  'Just for grown-ups': '大人專用',
  'One moment…': '請稍候…',
  'Forgot it?': '忘記了嗎？',
  'Sign in with your Google account again. Then you can make a new grown-up PIN.':
    '請再用您的 Google 帳號登入一次，然後就可以設定新的大人 PIN 碼。',
  'What year were you born?': '您是哪一年出生的？',
  'Next': '下一步',
  'Grown-up PIN': '大人 PIN 碼',
  'Year you were born': '您的出生年份',
  'PIN': 'PIN 碼',
  'Make a random one': '隨機產生',

  // Consent and child set-up
  'Please read this once before your child starts.': '在孩子開始使用前，請先閱讀說明。',
  'Set up a child': '建立孩子資料',
  'Month': '月',
  'Year': '年',
  'That month has not happened yet.': '那個月份還沒到。',
  'Each child has a PIN': '每個孩子都有一組 PIN 碼',
  "They type it before their practice opens, so brothers and sisters don't answer each other's questions. Write these down now — they can't be shown again. Any child not listed here can be given a PIN in Settings, and you can set new ones there at any time.":
    '孩子開始練習前要先輸入，這樣兄弟姊妹就不會替彼此作答。請現在把它們記下來，之後無法再顯示。這裡沒有列出的孩子，可以在設定中給 PIN 碼，您也隨時可以在那裡重設。',
  "I've written them down": '我已經記下來了',
  'Saved so far:': '目前已儲存：',
  '. Fill this in for the next child, or press Save and finish.': '。請填寫下一個孩子的資料，或按「儲存並完成」。',
  'Save and add another child': '儲存並新增另一個孩子',
  'Save and finish': '儲存並完成',
  'This decides how hard the work starts out. You can change it any time.': '這會決定一開始題目的難度，之後隨時可以更改。',
  'First name': '名字',
  'Born': '出生年月',
  'How far ahead or behind should the work sit?': '題目要比年級超前或落後多少？',
  'First language': '母語',

  // Progress and email details (Settings)
  'Fully grown:': '完全學會：',
  'Met for the first time:': '第一次遇到：',
  "Saved. Email isn't connected yet, so nothing will be sent until it is — you can read what it would say with Preview.":
    '已儲存。電子郵件還沒有連接，所以在連接之前不會寄出；您可以用「預覽」看看內容。',
  'Subject:': '主旨：',
  'Words in the pool': '單字庫中的字',
  'Questions answered': '已回答的題目',
  'Day streak': '連續天數',
  'Recent accuracy': '最近的正確率',
  'First day': '首次練習日',
  'How often': '發送頻率',
  "This week's email": '本週的電子郵件',
  // Third pass (25 Oct evening): consent buttons, set-up errors and labels, months
  "Saving…": "儲存中…",
  "Continue": "繼續",
  "Version {v}. If this text ever changes, we will ask again.": "第 {v} 版。如果這段文字有更改，我們會再詢問您。",
  "A first name is enough.": "填名字就可以了。",
  "Pick a year as well.": "也請選擇年份。",
  "Pick a month as well.": "也請選擇月份。",
  "Work pitched at school year {year}.": "題目程度為 {year} 年級。",
  "Work pitched at year {year} — {n} ahead.": "題目程度為 {year} 年級，超前 {n} 年。",
  "Work pitched at year {year} — {n} behind.": "題目程度為 {year} 年級，落後 {n} 年。",
  "At year": "同年級",
  "English only": "只說英文",
  "That did not work.": "沒有成功。",
  "Child {n}": "第 {n} 個孩子",
  "January": "一月",
  "February": "二月",
  "March": "三月",
  "April": "四月",
  "May": "五月",
  "June": "六月",
  "July": "七月",
  "August": "八月",
  "September": "九月",
  "October": "十月",
  "November": "十一月",
  "December": "十二月",

  // Round 13 (28 Oct 2026): the rest of the grown-up screens. Drafted by
  // Codex (research/translation-gaps/), checked and completed by Claude.
  '1 book': '1 本書',
  '1 word from it': '從中挑了 1 個字',
  '1 word grown': '已學會 1 個字',
  'A PIN is set.': '已設定 PIN 碼。',
  'A photo, seen only by your family.': '這張照片只有您的家人看得到。',
  'Add 1 word': '加入 1 個字',
  'Add a book': '新增書本',
  'Add a photo': '新增照片',
  'Add the first book': '新增第一本書',
  'Add {n} words': '加入 {n} 個字',
  'All the same number is too easy to guess. Try a mix.': '全部相同太容易猜，請混合不同數字。',
  'Allow it anyway': '仍要允許',
  'Archive {name}? Their words and answers are kept, but they leave the home screen.': '要封存 {name} 嗎？單字和作答都會保留，但首頁不再顯示。',
  'Author A–Z': '作者 A–Z',
  'Book': '書本',
  'Change Sprout Buddy': '更換 Sprout Buddy',
  'Change photo': '更換照片',
  'Checked 1 word learned earlier: still known.': '複習了以前學過的 1 個字：仍然記得。',
  'Checked {n} words learned earlier: still known.': '複習了以前學過的 {n} 個字：仍然記得。',
  'Checking…': '正在檢查…',
  'Choose a grown-up PIN, 4 to 6 numbers.': '設定 4 到 6 位數的大人 PIN 碼。',
  'Choose a meaning for each word': '為每個字選擇意思',
  'Close': '關閉',
  'Come back after a few sessions.': '完成幾次練習後再回來看看。',
  'Come back during practice time ({zone}):': '請在練習時間回來（{zone}）：',
  'Copy the words from one page of {title} with your phone\'s camera, then paste them here. You pick the ones worth learning.': '用手機相機複製《{title}》其中一頁的文字，再貼到這裡。由您挑選值得學的字。',
  'Could not reach the payment service. Try again.': '無法連上付款服務，請再試一次。',
  'Counting up or down is too easy to guess. Try a mix.': '連續遞增或遞減太容易猜，請混合不同數字。',
  'Delete': '刪除',
  'Done': '完成',
  'Each child types their own PIN before practising.': '每個孩子開始練習前，都要輸入自己的 PIN 碼。',
  'Every book is used once to pick words before any is used twice.': '每本書都會先用來挑一次單字，才會有書使用第二次。',
  'Everyone': '所有人',
  'Film': '電影',
  'Find words': '找單字',
  'Finding words…': '正在找單字…',
  'Finished it': '讀完了',
  'First, make a grown-up PIN. It keeps Settings, and each child\'s PIN, for grown-ups.': '請先設定大人的 PIN 碼，讓設定和每個孩子的 PIN 碼只有大人能操作。',
  'For a grown-up': '給大人查看',
  'Free trial — ends {date}.': '免費試用，於 {date} 結束。',
  'Give the book a name, or scan its barcode.': '請輸入書名，或掃描條碼。',
  'Go home': '回首頁',
  'Help {name} choose a PIN': '陪 {name} 選一個 PIN 碼',
  'Hold the barcode inside the box.': '請把條碼對準框內。',
  'How to copy on Android': '如何在 Android 上複製',
  'How to copy on an iPhone or iPad': '如何在 iPhone 或 iPad 上拷貝',
  'Least used': '使用次數最少',
  'Left out': '未列入',
  'Loading': '正在載入',
  'Look it up': '查詢',
  'Make a Sprout Buddy': '製作 Sprout Buddy',
  'Minutes each day': '每天的分鐘數',
  'Minutes each week': '每週的分鐘數',
  'Most children start at their school year. Move it if the work turns out too easy or too hard.': '多數孩子從自己的年級開始。如果題目太簡單或太難，再調整即可。',
  'New code': '新代碼',
  'New trophies this month': '這個月的新獎盃',
  'New trophies this week': '這週的新獎盃',
  'Newest added': '最近新增',
  'No PIN yet.': '尚未設定 PIN 碼。',
  'No barcode, or a film or show? Type its name →': '沒有條碼，或是電影、節目？請輸入名稱 →',
  'No books yet. Type the 13 digits under the barcode on the back, or scan it. We look it up in two free book catalogues (Open Library and Google Books); nothing is guessed.': '還沒有書。請輸入封底條碼下方的 13 位數字，或掃描條碼。我們會到兩個免費書目（Open Library 和 Google Books）查詢，不會猜測。',
  'No connection to Google. Check the network and try again.': '無法連上 Google。請檢查網路後再試一次。',
  'No internet': '沒有網路',
  'None this month.': '這個月沒有。',
  'None this week.': '這週沒有。',
  'Not used to pick words yet': '尚未用來挑選單字',
  'Nothing tricky this month.': '這個月沒有特別難的字。',
  'Nothing tricky this week.': '這週沒有特別難的字。',
  'Oldest added': '最早新增',
  'On {phone}, open Word Sprout, then Settings → Your account → {add}. Type the six numbers it shows here. No Google sign-in happens on this tablet.': '在{phone}上開啟 Word Sprout，前往「設定」→「您的帳號」→「{add}」。在這裡輸入它顯示的六個數字。這台平板不會登入 Google。',
  'One word added.': '已加入一個字。',
  'One word at a time, please — letters only.': '請一次輸入一個字，而且只能使用字母。',
  'Open the Camera and point it at the page. Tap the Live Text button (lines in a frame) at the bottom corner. Tap Select All, then Copy. Come back here and paste.': '開啟「相機」並對準書頁。點選角落的「原況文字」按鈕（框內有幾條線），再點「全選」和「拷貝」。回到這裡貼上。',
  'Optional, and month and year only. We never ask for the exact date.': '選填，只需要月份和年份。我們不會詢問確切日期。',
  'Paste the words from one page first.': '請先貼上一頁的文字。',
  'Payments are not switched on.': '付款功能尚未開啟。',
  'Photos are seen only by your family, with location details removed.': '照片只有您的家人看得到，而且位置資訊會被移除。',
  'Pick a picture, or make a Sprout Buddy.': '選一張圖片，或製作 Sprout Buddy。',
  'Puts a small label on every value, form and answer in the app, naming the table it comes from or is saved to. A dashed label is read from that table; a solid one with an arrow (→) is saved to it. Click any label to open the data map at that table. It stays on, on this device, until you turn it off here.': '在程式中的每個數值、表單和答案旁放上小標籤，說明資料來自或存入哪個資料表。虛線標籤表示從資料表讀取；帶箭頭（→）的實線標籤表示存入資料表。點選標籤可在資料地圖中開啟該資料表。除非您在這裡關閉，這台裝置會一直顯示標籤。',
  'Reading it again': '正在重讀',
  'Remove photo': '移除照片',
  'Round {round}: {used} of {approved} books used': '第 {round} 輪：已使用 {used}/{approved} 本',
  'Scan it': '掃描條碼',
  'Show as a chart': '顯示為圖表',
  'Show as a table': '顯示為表格',
  'Sign a child\'s tablet in without Google. On the tablet, tap {button} on the Word Sprout front page, then type the code shown here. A tablet set up this way can practise, but cannot reach billing or delete the account.': '免用 Google 帳號讓孩子平板登入。請在平板上的 Word Sprout 首頁點選「{button}」，然後輸入這裡顯示的代碼。用這種方式設定的平板可以練習，但不能進入付費設定，也不能刪除帳號。',
  'Sign in again with the grown-up\'s Google account.': '請再用大人的 Google 帳號登入。',
  'Sign in this tablet': '讓這台平板登入',
  'Sign in with Google': '使用 Google 登入',
  'Sign out "{label}"? It will need a new code to come back.': '要登出「{label}」嗎？之後需要新的代碼才能再登入。',
  'Something went wrong': '發生問題',
  'Starting the camera…': '正在啟動相機…',
  'Subscribed — renews {date}.': '已訂閱，於 {date} 續訂。',
  'TV show': '電視節目',
  'Take a photo of the page and open it in Google Photos. Tap Lens, then Text, select the words and tap Copy. Google, not Word Sprout, reads the photo.': '拍下書頁並在 Google 相簿中開啟。點選「智慧鏡頭」，再選「文字」，選取內容後點「複製」。照片由 Google 讀取，不會交給 Word Sprout。',
  'Text from one page': '一頁的文字',
  'That code did not work. Check the six numbers, or ask for a new code.': '代碼無效。請檢查六位數字，或取得新代碼。',
  'That did not look like a picture.': '這個檔案看起來不是照片。',
  'That did not save.': '沒有儲存成功。',
  'That did not work. Check the connection and try again.': '沒有成功。請檢查網路後再試一次。',
  'That did not work. Try again in a moment.': '沒有成功，請稍後再試。',
  'That did not work. Try again.': '沒有成功，請再試一次。',
  'That does not look like a word.': '這看起來不像一個字。',
  'That file was empty.': '這個檔案是空的。',
  'That is more than one page. Paste one page at a time.': '內容超過一頁，請一次只貼一頁。',
  'That is not a grown-up\'s year.': '這不像是大人的出生年份。',
  'That is not a valid ISBN. Check the last digit.': '這不是有效的 ISBN，請檢查最後一位。',
  'That is not a valid ISBN. It is the 13 digits under the barcode — check the last one.': '這不是有效的 ISBN。ISBN 是條碼下方的 13 位數字，請檢查最後一位。',
  'That one is not available in this app.': '這個字無法在本程式中使用。',
  'That picture did not upload.': '照片上傳失敗。',
  'That picture is too big. Try one under 8 MB.': '照片太大，請使用小於 8 MB 的檔案。',
  'That would make them over 25. Check the year.': '這樣孩子會超過 25 歲，請檢查年份。',
  'That\'s not the grown-up PIN.': '這不是大人的 PIN 碼。',
  'The Google window was closed before signing in.': 'Google 視窗在登入完成前關閉了。',
  'The TV show\'s name': '節目名稱',
  'The barcode reader could not be loaded. Type the number instead — it is printed under the barcode.': '無法載入條碼讀取器。請改為輸入條碼下方印的數字。',
  'The book\'s name': '書名',
  'The browser blocked the Google window. Allow pop-ups for this site and try again.': '瀏覽器擋住了 Google 視窗。請允許此網站開啟彈出式視窗，再試一次。',
  'The camera could not be opened. It may be in use, or permission was refused.': '無法開啟相機。相機可能正在使用中，或您沒有允許存取。',
  'The film\'s name': '電影名稱',
  'There is already a child with that name.': '已經有同名的孩子。',
  'There is no subscription to manage yet.': '目前還沒有可管理的訂閱。',
  'This is a child\'s tablet. Clear the grown-up PIN from Settings on your own phone.': '這是孩子的平板。請在您自己的手機上，到設定中清除大人 PIN 碼。',
  'This is a child\'s tablet: {device}': '這是孩子的平板：{device}',
  'This web address is not authorised for sign-in yet.': '這個網址尚未獲准用於登入。',
  'Those two were different. Choose again.': '兩次輸入不同，請重新設定。',
  'Tick the ones you want': '勾選想要的字',
  'Title A–Z': '書名 A–Z',
  'To make a new grown-up PIN, answer this first.': '要設定新的大人 PIN 碼，請先回答這個問題。',
  'Too many tries. Wait a minute, then try again.': '嘗試次數太多。請等一分鐘再試。',
  'Too many tries. Wait fifteen minutes, then try again.': '嘗試次數太多。請等十五分鐘再試。',
  'Too many tries. Wait an hour, then try again.': '嘗試次數太多。請等一小時再試。',
  "Too many book lookups this hour. Type the book's name instead, or try again later.": '這一小時查詢書籍的次數太多。請改為輸入書名，或稍後再試。',
  'That is a lot of new words for one hour. Try again later.': '一小時內的新單字太多了。請稍後再試。',
  'Too many wrong codes. Wait ten minutes, then ask for a new code.': '錯誤代碼次數太多。請等十分鐘，再取得新代碼。',
  'Try again': '再試一次',
  'Type a word first.': '請先輸入一個字。',
  'Type it once more.': '請再輸入一次。',
  'Type the 13 digits under the barcode on the back, or scan it. We look it up in two free book catalogues (Open Library and Google Books); nothing is guessed.': '請輸入封底條碼下方的 13 位數字，或掃描條碼。我們會到兩個免費書目（Open Library 和 Google Books）查詢，不會猜測。',
  'Type the grown-up PIN.': '請輸入大人的 PIN 碼。',
  'Use a JPEG, PNG, WEBP or GIF.': '請使用 JPEG、PNG、WEBP 或 GIF。',
  'Use a different page': '換一頁',
  'Use it after all': '還是使用',
  'Used for the translation button, and spoken in that language.': '用於翻譯按鈕，語音也會使用這個語言。',
  'Used once to pick words · last {date}': '用來挑選單字 1 次 · 上次為 {date}',
  'Used {n} times to pick words · last {date}': '用來挑選單字 {n} 次 · 上次為 {date}',
  'Waiting for a grown-up ({n})': '等待大人確認（{n}）',
  'We found no words worth adding on that page. Try a page with more going on.': '這一頁沒有找到值得加入的字。請試試內容較多的一頁。',
  'What each child practised, the words that grew, the ones still tricky (with exactly how tricky), their tree and trophies, and one word to ask them about. Weekly unless you change it. Sent to {email} on Monday mornings.': '每個孩子練習了什麼、學會了哪些字、哪些字還不太熟（以及具體有多不熟）、他們的樹和獎盃，以及一個可以問孩子的字。除非您更改設定，否則每週一早上寄到 {email}。',
  'Which meaning is used here?': '這裡用的是哪個意思？',
  'Word Sprout gets the text you paste, never the photo. It is used to suggest words and is not kept.': 'Word Sprout 只會收到您貼上的文字，不會收到照片。文字只用來建議單字，不會保留。',
  'Word Sprout is asleep': 'Word Sprout 正在休息',
  'Word Sprout needs the internet to work. Check the connection and try again.': 'Word Sprout 需要網路才能運作。請檢查連線後再試一次。',
  'Words from a page': '從書頁挑選單字',
  'Works once, for {time} more': '只能使用一次，有效時間還剩 {time}',
  'Write it down now — it can\'t be shown again. You can always set a new one.': '請現在記下來，之後無法再顯示。您隨時可以設定新的 PIN 碼。',
  'about grade {grade}': '約 {grade} 年級',
  'added {date}': '新增於 {date}',
  'already has it': '已經有這個字',
  'author unknown': '作者不詳',
  'borrowed {date}': '借於 {date}',
  'from the library': '來自圖書館',
  'from the old app’s book list': '來自舊版程式的書單',
  'last month: {n}': '上個月：{n}',
  'last week: {n}': '上週：{n}',
  'min': '分鐘',
  'missed {missed} of {seen}': '{seen} 次中答錯 {missed} 次',
  'none this week': '本週沒有',
  'none yet this week': '本週還沒有',
  'not for this app': '不適合這個程式',
  'not used yet': '尚未使用',
  'typed in': '手動輸入',
  'used today': '今天使用過',
  'used yesterday': '昨天使用過',
  'used {n} days ago': '{n} 天前使用過',
  'words': '單字',
  'your email': '您的電子郵件',
  '{name} chose a new PIN.': '{name} 已設定新的 PIN 碼。',
  '{name} forgot their PIN. A grown-up can help them choose a new one.': '{name} 忘記了 PIN 碼。大人可以陪孩子選一個新的。',
  'Only a grown-up can add a child.': '只有大人可以新增孩子。',
  '{name} needs a PIN first. A grown-up helps them choose one.': '{name} 需要先設定 PIN 碼，由大人陪著一起選。',
  '{name}\'s tree': '{name} 的樹',
  '{n} books': '{n} 本書',
  '{n} meanings': '{n} 個意思',
  '{n} more to {next}': '再 {n} 個就會長成{next}',
  '{n} pages': '{n} 頁',
  '{n} words added.': '已加入 {n} 個字。',
  '{n} words from it': '從中挑了 {n} 個字',
  '{n} words grown': '已學會 {n} 個字',
  '“{title}” is on the shelf.': '「{title}」已放到書架上。',
  '“{word}” added.': '已加入「{word}」。',
  '← Home': '← 首頁',

  // Round 14 (28 Oct 2026): the front page, sign-in, the agreement and the
  // free trial, now that a language can be picked before sign-in.
  'Growing one word at a time. A few minutes a day.': '一次長出一個字，每天只要幾分鐘。',
  'Made by a parent, for word-loving kids.': '一位家長，為愛字的孩子而做。',
  'Their own words': '孩子自己的單字',
  'Back again until it sticks': '反覆出現，直到記住',
  'Grows only with real learning': '真正學會才會長大',
  'Honest reports for you': '給您的誠實報告',
  'No ads': '沒有廣告',
  'No streaks to keep': '不用維持連續天數',
  'Nothing to buy inside': '程式內沒有東西要買',
  'Answers stay private': '作答資料保持私密',
  'Try it free': '免費試用',
  'Sign in': '登入',
  'Already have an account?': '已經有帳號了嗎？',
  "Setting up a child's tablet?": '要設定孩子的平板嗎？',
  'Free for two weeks. Cancel any time.': '前兩週免費，隨時可以取消。',
  'We are not taking new families just yet.': '我們目前還沒有開放新家庭加入。',
  'Word Sprout is being tested with a small group first. If your family already has an account, sign in here.': 'Word Sprout 目前先由一小群家庭試用。如果您的家庭已經有帳號，請在這裡登入。',
  'How it works': '使用方式',
  'Sign-in is not finished being set up on the server.': '伺服器上的登入功能還沒設定完成。',
  'Reading the agreement': '正在讀取同意書',
  'Before you start': '開始之前',
  'A computer writes the sentences, examples, comics and jokes. It is usually good. Sometimes it is odd or wrong. Look at what your child is being shown.': '句子、例句、漫畫和笑話是由電腦寫的。內容通常不錯，但有時會奇怪或出錯。請留意孩子看到的內容。',
  "The voice is your device's own computer voice. It can mispronounce unusual words.": "語音是您裝置本身的電腦語音，不常見的字可能會唸錯。",
  'We keep what your child answers. Every attempt, right or wrong, with the time it took. That is how the app decides what to show next. It is the whole point.': '我們會保留孩子的作答：每一次嘗試，不論對錯，以及花了多少時間。程式就是靠這些決定接下來出什麼題目，這正是它的用意。',
  "We do not sell it, and there are no ads.": "我們不會販售這些資料，也沒有廣告。",
  "A few services help run the app, and each gets only what it needs. Google Cloud keeps the app and its data. Resend sends our emails to you; progress emails include your child's first name and words. When you add a book, its number goes to Open Library and Google Books to find it. When your child asks for a word, the word goes to Wiktionary or Datamuse for its meaning. None of them is told who your child is.": "有幾個服務協助應用程式運作，每個服務只會拿到它需要的資料。Google Cloud 保存應用程式和資料。Resend 替我們寄電子郵件給您；學習進度郵件會包含孩子的名字和單字。您新增書本時，書號會送到 Open Library 和 Google Books 來查詢。孩子要求新單字時，這個單字會送到 Wiktionary 或 Datamuse 查詢意思。這些服務都不會知道您的孩子是誰。",
  "You can delete everything at any time in Settings. It leaves the app at once; our safety backups keep a copy for up to 30 days. To see what we keep about your child, email us at hello@wordsprout.net.": "您隨時可以在「設定」中刪除全部資料，資料會立即從應用程式移除；我們的安全備份最多保留 30 天。想查看我們保存了孩子的哪些資料，請寫信到 hello@wordsprout.net。",
  'This is a practice tool. It is not a school curriculum, and it does not diagnose anything.': '這是練習工具，不是學校課程，也不做任何診斷。',
  'You are the adult in the room. You agree to supervise, and you accept the risk of using it.': '您是在場的大人。您同意在旁照看，並承擔使用本程式的風險。',
  'I have read this and I agree.': '我已閱讀並同意以上內容。',
  'Finishing up — this takes a moment': '快完成了，請稍等一下',
  'the monthly price': '每月費用',
  'Start your free trial': '開始免費試用',
  '{n} days free, then {price}.': '{n} 天免費，之後 {price}。',
  'Every child in your family, one price.': '全家每個孩子都能用，只付一份價錢。',
  'Progress emails, the word universe, the trophy room — all of it.': '進度電子郵件、單字宇宙、獎盃室，全部都有。',
  'No ads, and nothing to buy inside the app.': '沒有廣告，程式裡也沒有東西要買。',
  'Cancel any time, in two taps, from Settings.': '隨時可以在「設定」裡取消，只要點兩下。',
  'Opening the secure payment page…': '正在開啟安全付款頁面…',
  'Start {n} days free': '開始 {n} 天免費試用',
  "You'll add a card on Stripe's secure page. Nothing is charged until the {n} days are up, and you can cancel before then.": '您會在 Stripe 的安全頁面加入信用卡。{n} 天結束前不會收費，在那之前也可以取消。',
  'No card needed to start. At the end of the trial you can add one to carry on.': '開始時不需要信用卡。試用結束時，可以加入信用卡繼續使用。',
  'Your card goes to Stripe, never to Word Sprout.': '您的信用卡資料只交給 Stripe，從不交給 Word Sprout。',
  "Payments aren't switched on yet, so a trial can't start. Please try again soon.": '付款功能還沒開啟，所以還不能開始試用。請過一陣子再試。',
  "No problem — nothing was charged. Start whenever you're ready.": '沒關係，沒有收取任何費用。準備好了隨時可以開始。',
  'Sign out': '登出',
  'The payment service did not accept that. Try again.': '付款服務沒有接受這項操作，請再試一次。',

  // Round 14: messages the server sends that a grown-up can read. A message
  // with a word inside is looked up by its sentence with the gap (api/said.py).
  'Something went wrong. Try again.': '發生問題，請再試一次。',
  'busy, try again': '系統忙碌中，請再試一次。',
  'not signed in': '尚未登入。',
  'no such child': '找不到這個孩子。',
  'no such book': '找不到這本書。',
  'No sign-in token was sent.': '沒有收到登入資料。',
  'That sign-in could not be verified. Try again.': '無法確認這次登入，請再試一次。',
  'That Google account has no verified email address.': '這個 Google 帳號沒有已驗證的電子郵件地址。',
  'That Google account has no email address.': '這個 Google 帳號沒有電子郵件地址。',
  'That account is not set up for Word Sprout. Ask the grown-up who set this up to add it.': '這個帳號還沒有加入 Word Sprout。請負責設定的大人把它加進來。',
  'Work can be pitched at most 3 years ahead or behind.': '程度最多只能比學年高或低 3 年。',
  'There is already a child called {name}.': '已經有一個叫 {name} 的孩子了。',
  'pick one of the offered pictures': '請從提供的圖片中選一張。',
  'A PIN is 4 or 5 or 6 numbers.': 'PIN 碼是 4、5 或 6 位數字。',
  'You already have “{word}”. Look for it in your garden.': '「{word}」已經在花園裡了。',
  'That one is not for this app. Ask a grown-up if you want to know what it means.': '這個字不適合本程式。如果想知道它的意思，請去問大人。',
  'We could not find “{word}” in the dictionary. Check the spelling?': '字典裡找不到「{word}」。請檢查一下拼字。',
  'No catalogue has {isbn}. You can still add the book by typing its name.': '書目資料庫裡找不到 {isbn}。您還是可以輸入書名來加入這本書。',
  "Photographing a page has been replaced. Reload the app, then copy the page's words with your phone's camera and paste them in.": '拍攝書頁的功能已經換掉了。請重新載入程式，再用手機相機複製書頁上的字，貼到這裡。',
  // Buddy maker and practice hours on grown-up screens; buddy server message (29 Oct 2026)
  "{name}'s Sprout Buddy": "{name} 的 Sprout Buddy",
  "Parts": "部位",
  "Groups": "類別",
  "Top": "頭頂",
  "Skin": "外皮",
  "Arms & legs": "手腳",
  "Holding": "手上拿的",
  "Plants": "植物",
  "Fun": "好玩",
  "Everyday": "日常",
  "Gems": "寶石",
  "Metals": "金屬",
  "Space": "太空",
  "Nature": "自然",
  "Winning": "勝利",
  "Calling you": "叫你過來",
  "Showing off": "炫耀",
  "Two leaves": "兩片葉子",
  "Oak": "橡樹",
  "Clover": "幸運草",
  "Fiddlehead": "蕨芽",
  "Palm": "棕櫚",
  "Fern": "蕨類",
  "Maple": "楓葉",
  "Herbs": "香草",
  "Succulent": "多肉植物",
  "Lightning": "閃電",
  "Propeller": "螺旋槳",
  "Star": "星星",
  "Sunflower": "向日葵",
  "Crown": "皇冠",
  "Antennae": "觸角",
  "Tangerine": "橘子",
  "Peach": "蜜桃",
  "Berry": "莓果",
  "Sunny": "陽光",
  "Sky": "天空",
  "Mint": "薄荷",
  "Grape": "葡萄",
  "Cocoa": "可可",
  "Diamond": "鑽石",
  "Ruby": "紅寶石",
  "Emerald": "祖母綠",
  "Sapphire": "藍寶石",
  "Amethyst": "紫水晶",
  "Topaz": "黃玉",
  "Gold": "黃金",
  "Titanium": "鈦",
  "Bronze": "青銅",
  "Galaxy": "銀河",
  "Nebula": "星雲",
  "Supernova": "超新星",
  "Black hole": "黑洞",
  "Portal": "傳送門",
  "Wood": "木頭",
  "Ice": "冰",
  "Lava": "岩漿",
  "Fire": "火焰",
  "Root": "樹根",
  "Rainbow": "彩虹",
  "Candy": "糖果",
  "Slime": "史萊姆",
  "Camo": "迷彩",
  "None": "無",
  "Arms and legs": "有手有腳",
  "Waving": "揮手",
  "Gardener": "園丁",
  "Victory fist": "勝利握拳",
  "Victory jump": "勝利跳躍",
  "V sign": "比 YA",
  "Thumbs up": "比讚",
  "Come on!": "來吧！",
  "I want you!": "就是你！",
  "Superhero": "超級英雄",
  "Dab": "Dab 姿勢",
  "Heart hands": "比愛心",
  "Power stance": "力量姿勢",
  "Casting a spell": "施魔法",
  "Watering can": "澆水壺",
  "Shovel": "鏟子",
  "Pencil": "鉛筆",
  "Save my buddy": "儲存 Sprout Buddy",
  "Surprise me": "隨機選一個",
  "Back": "返回",
  "That did not save. Try again.": "儲存失敗，請再試一次。",
  "{pose} uses both hands. Picking something to hold changes the arms to {arms}, so a hand is free.": "「{pose}」要用兩隻手。選一樣東西拿著，手臂會改成「{arms}」，空出一隻手。",
  "that is not a Sprout Buddy": "這不是 Sprout Buddy。",
  "New York time": "紐約時間",
  "Monday to Friday": "週一至週五",
  "7:00 to 8:35 in the morning": "早上 7:00 至 8:35",
  "Tuesday and Thursday": "週二和週四",
  "4:25 to 5:15 in the afternoon": "下午 4:25 至 5:15",
  "Saturday and Sunday": "週六和週日",
  "7:30 to 10:30 in the morning": "早上 7:30 至 10:30",
  // Starting-point check, email-plus consent, trial on this device (29 Oct 2026)
  "I'm the grown-up": "我是大人",
  "To see the starting-point check.": "查看起點檢測的結果。",
  "kindergarten": "幼兒園",
  "year {n}": "{n} 年級",
  "A year below ({year})": "低一個年級（{year}）",
  "Their school year ({year})": "目前的年級（{year}）",
  "A year above ({year})": "高一個年級（{year}）",
  "Beta: new, and not yet tested with many children. It only picks a place to start.": "測試版：這是新功能，還沒有很多孩子用過。它只是幫忙選一個起點。",
  "{name}'s starting-point check": "{name} 的起點檢測",
  "{n} of {total} right": "答對 {n} 題，共 {total} 題",
  "Suggested start: {year} words.": "建議從{year}的單字開始。",
  "The answers did not agree with each other, so this suggestion is less sure. Starting at the school year is the safer choice.": "這次的答案彼此不太一致，所以這個建議比較不確定。從目前的年級開始比較穩妥。",
  "Saved: {year} words. New words will move to this level over the next few practices. Settings suggests a change if they turn out too easy or too hard.": "已儲存：{year}的單字。接下來幾次練習，新單字會慢慢換到這個程度。如果太簡單或太難，設定頁會建議調整。",
  "Use the suggestion: {year}": "採用建議：{year}",
  "One year easier: {year}": "簡單一個年級：{year}",
  "One year harder: {year}": "難一個年級：{year}",
  "Keep what it is now: {year}": "維持現在的設定：{year}",
  "Six words a day, for as long as you like. No account, and nothing your child does is sent to us.": "每天六個單字，想試多久都可以。不用帳號，孩子做的任何事都不會傳給我們。",
  "Try Word Sprout on this device": "在這台裝置上試用 Word Sprout",
  "Your child's school year": "孩子的年級",
  "Year {n}": "{n} 年級",
  "What your child answers stays in this browser on this device. Word Sprout only sends the words for the school year you pick, the same words anyone gets. You can delete it all here at any time.": "孩子的答案只會留在這台裝置的瀏覽器裡。Word Sprout 只會傳來您選的年級的單字，每個人拿到的都一樣。您隨時可以在這裡全部刪除。",
  "Hand it to my child": "交給孩子",
  "For the grown-up": "給大人看",
  "Kept on this device only: {words} words met. Nothing was sent to Word Sprout.": "只存在這台裝置上：學過 {words} 個字。沒有任何資料傳給 Word Sprout。",
  "Make an account to keep going": "建立帳號繼續使用",
  "Delete everything this trial kept on this device?": "要刪除這次試用存在這台裝置上的所有資料嗎？",
  "Delete it": "刪除",
  "Thank you. Your child can start now. Go back to Word Sprout on the device where you signed in, or open it here.": "謝謝您。孩子現在可以開始了。請回到您登入的那台裝置上的 Word Sprout，或在這裡打開。",
  "Done. Everything about your children has been deleted. We have sent you an email to say so.": "完成。關於您孩子的所有資料都已刪除，我們也寄了一封電子郵件通知您。",
  "Your consent for Word Sprout": "您對 Word Sprout 的同意",
  "Withdraw your consent": "撤回您的同意",
  "This link has expired or was already used. Open Word Sprout to get a new one.": "這個連結已過期或已經用過了。請打開 Word Sprout 取得新的連結。",
  "Do you agree that Word Sprout may collect what the email described from your child (first name, school year, pictures, PIN and answers) to choose their practice and show you their progress?": "您是否同意 Word Sprout 依照電子郵件所說，收集孩子的資料（名字、年級、圖片、PIN 碼和答案），用來安排練習並讓您看到進度？",
  "For {email}. You can withdraw at any time; a second email in a day or so says how.": "寄給 {email}。您隨時可以撤回；大約一天後的第二封電子郵件會說明怎麼做。",
  "Yes, I agree": "是的，我同意",
  "This deletes everything about your {n} children at once: names, pictures, answers and progress. It cannot be undone. Your own sign-in stays.": "這會立刻刪除您 {n} 個孩子的所有資料：名字、圖片、答案和進度，而且無法復原。您自己的登入帳號會保留。",
  "Withdraw and delete": "撤回並刪除",
  "Open Word Sprout": "打開 Word Sprout",
  "We sent a link to {email}. Open it, on any device, and press \"Yes, I agree\". Then come back here.": "我們寄了一個連結到 {email}。請在任何裝置上打開它，按下「是的，我同意」，然後回到這裡。",
  "Check your email": "請查看您的電子郵件",
  "I have agreed. Continue": "我已經同意了，繼續",
  "Send the email again": "再寄一次",
  "Not there? Look in spam or promotions. It comes from Word Sprout.": "沒收到嗎？請看看垃圾郵件或促銷郵件。寄件人是 Word Sprout。",
  "While you wait, your child can try Word Sprout on this device. Nothing they do is sent to us until you agree.": "等待的時候，孩子可以先在這台裝置上試用 Word Sprout。在您同意之前，孩子做的任何事都不會傳給我們。",
  "Let my child try it": "讓孩子試試看",
  "Email me the consent link": "寄同意連結給我",
  "To be sure you are the child's parent or guardian, we ask for your consent by email. Nothing is collected from your child before you agree.": "為了確認您是孩子的父母或監護人，我們會用電子郵件請您同意。在您同意之前，不會收集孩子的任何資料。",
  "Starting-point check (beta)": "起點檢測（測試版）",
  "About 5 minutes. {name} hears 20 words and taps what each one means. It suggests a level; you choose.": "大約 5 分鐘。{name} 會聽 20 個單字，點選每個字的意思。它會建議一個程度，由您決定。",
  "Last check: {date}": "上次檢測：{date}",
  "Start the check": "開始檢測",
  "A trial on this device": "這台裝置上的試用",
  "Before the account, this device kept a trial: {n} words, {m} of them missed. Add them to a child's list, the missed ones first?": "建立帳號之前，這台裝置存了一次試用：{n} 個字，其中 {m} 個答錯過。要把它們加進孩子的單字清單，答錯的排在前面嗎？",
  "{n} words added. The trial on this device is deleted.": "已加入 {n} 個字。這台裝置上的試用資料已刪除。",
  "Add them": "加入",
  "Delete the trial": "刪除試用資料",
  "Want to see it first?": "想先看看嗎？",
  "Try it on this device": "在這台裝置上試用",
  "The email has just been sent. Give it a minute, then check your spam folder.": "電子郵件剛剛寄出了。請等一分鐘，然後看看垃圾郵件匣。",
  "That is enough emails for today. Try again tomorrow.": "今天寄的郵件已經夠多了，請明天再試。",
  "There is no email address on this account.": "這個帳號沒有電子郵件地址。",
  "There are not enough words for this school year yet.": "這個年級的單字還不夠。",
  // Codex round 16 fixes (29 Oct 2026)
  "The agreement has changed. Please read it again.": "同意書已更新，請重新閱讀。",
  "The starting-point check works for school years 1 to 11 for now.": "起點檢測目前只適用於 1 到 11 年級。",
  "There is a newer check for this child. Use that one.": "這個孩子有一次較新的檢測，請使用那一次。",
  "The answers do not match the check.": "答案與這次檢測不相符。",
  "The check is not finished yet.": "檢測還沒有完成。",
  "This link has already been used, or is not a Word Sprout link.": "這個連結已經用過了，或不是 Word Sprout 的連結。",
}
